// @greenlight-spec: 28-it-dashboard-ui.md
import {
  AppWindow,
  Bot,
  CircleDollarSign,
  ClipboardCheck,
  Cog,
  HeartPulse,
  Library,
  MessageSquare,
  Plug,
  ShieldCheck,
  Store,
  Users,
  type LucideIcon,
} from 'lucide-react';

import type { Role } from './auth';

/** Extra context nav visibility can depend on, beyond role. */
export interface NavOptions {
  /** `cost-management` preview gate: `/cost` is held back from release builds. */
  costManagementEnabled?: boolean;
  /** `greenlight-agents` preview gate: `/agents` renders on fixture data only. */
  greenlightAgentsEnabled?: boolean;
}

export interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  roles: readonly Role[];
  bottomNav?: boolean;
  bottomNavOrder?: number;
  /** Extra visibility gate beyond role (e.g. an org-wide rollout flag). Always visible when absent. */
  isEnabled?: (opts: NavOptions) => boolean;
}

export const NAV_ITEMS: readonly NavItem[] = [
  {
    to: '/apps',
    label: 'Apps',
    icon: AppWindow,
    roles: ['org_admin', 'admin', 'user'],
    bottomNav: true,
    bottomNavOrder: 1,
  },
  {
    to: '/marketplace',
    label: 'Marketplace',
    icon: Store,
    roles: ['org_admin', 'admin', 'user'],
    bottomNav: true,
    // After Apps/Integrations/Audit (docs/28's IT trio) so IT's existing bottom-nav
    // stays unchanged; for `user` (fewer competing slots) this lands in their top 3.
    bottomNavOrder: 4,
  },
  {
    to: '/integrations',
    label: 'Integrations',
    icon: Plug,
    roles: ['org_admin', 'admin'],
    bottomNav: true,
    bottomNavOrder: 2,
  },
  {
    to: '/knowledge',
    label: 'Knowledge',
    icon: Library,
    roles: ['org_admin', 'admin'],
  },
  {
    to: '/users',
    label: 'Users & RBAC',
    icon: Users,
    roles: ['org_admin'],
  },
  {
    to: '/audit',
    label: 'Audit',
    icon: ShieldCheck,
    roles: ['org_admin', 'admin'],
    bottomNav: true,
    bottomNavOrder: 3,
  },
  {
    to: '/compliance',
    label: 'Compliance',
    icon: ClipboardCheck,
    roles: ['org_admin', 'admin'],
  },
  {
    to: '/agents',
    label: 'Greenlight Agents',
    icon: Bot,
    roles: ['org_admin', 'admin'],
    isEnabled: (opts) => opts.greenlightAgentsEnabled ?? false,
  },
  {
    to: '/internal-health',
    label: 'Internal Health',
    icon: HeartPulse,
    roles: ['org_admin'],
  },
  {
    to: '/cost',
    label: 'Cost',
    icon: CircleDollarSign,
    roles: ['org_admin'],
    isEnabled: (opts) => opts.costManagementEnabled ?? false,
  },
  {
    to: '/feedback',
    label: 'Feedback',
    icon: MessageSquare,
    roles: ['org_admin'],
  },
  {
    to: '/settings',
    label: 'Settings',
    icon: Cog,
    roles: ['org_admin', 'admin', 'user'],
  },
];

/** The Apps destination reads "My Apps" for `user` (docs/42 §Navigation) —
 *  IT keeps the org-wide "Apps" label since their view is the whole fleet. */
function labelFor(item: NavItem, role: Role): string {
  return item.to === '/apps' && role === 'user' ? 'My Apps' : item.label;
}

export function visibleNavItems(role: Role, opts: NavOptions = {}): NavItem[] {
  return NAV_ITEMS.filter(
    (item) => item.roles.includes(role) && (item.isEnabled?.(opts) ?? true),
  ).map((item) => ({ ...item, label: labelFor(item, role) }));
}

export interface BottomNavSlot {
  to: string;
  label: string;
  icon: LucideIcon;
}

// The bottom nav surfaces the three most-used destinations for the role; "More"
// (rendered by the nav itself) opens the drawer for everything else. IT roles
// get Apps · Integrations · Audit per the spec (Integrations now hosts the
// cross-app grants queue too); users — who never review grants or audit — get
// their own working set.
export function bottomNavSlots(role: Role, opts: NavOptions = {}): BottomNavSlot[] {
  const flagged = NAV_ITEMS.filter(
    (item) => item.bottomNav && item.roles.includes(role) && (item.isEnabled?.(opts) ?? true),
  ).sort((a, b) => (a.bottomNavOrder ?? 0) - (b.bottomNavOrder ?? 0));

  const slots: BottomNavSlot[] = flagged.map((item) => ({
    to: item.to,
    label: labelFor(item, role),
    icon: item.icon,
  }));

  if (role === 'user') {
    slots.push({ to: '/settings', label: 'Settings', icon: Cog });
  }

  return slots.slice(0, 3);
}
